import { configureUrlWithParams } from "../utils";
import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { fetchData } from "../offthreadFetcher";

type Params = {
  dashboard: string;
  dashboardWidget: string;
  widgetUrl: string;
  filters: Record<string, any>;
  /**
   * Whether to fetch data. When false, defers fetching until true.
   * Used for lazy loading - only fetch when widget is visible.
   * @default true
   */
  shouldFetch?: boolean;
};

interface AdditionalFilters {
  [key: string]: any;
}

interface ResponseType {
  result_meta?: {
    result_type?: string;
    override_message?: string;
    filtered_by?: any;
    tooltip_message?: string;
  };
  [key: string]: any;
}

interface CompanyDetails {
  time_zone_offset?: string;
  [key: string]: any;
}

interface CacheEntry {
  data: ResponseType;
  timestamp: number;
  timeoutId: NodeJS.Timeout;
}

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
// Bump when widget API payload shape changes (e.g. extended_analytics meta).
const WIDGET_RESPONSE_CACHE_VERSION = "v2";
const cache = new Map<string, CacheEntry>();

// Cache utility functions
const getCacheKey = (url: string): string => {
  return `${WIDGET_RESPONSE_CACHE_VERSION}:${url}`;
};

/** Reject legacy cached widget payloads missing Ext A / AI meta flags. */
const isValidWidgetResponseCache = (cached: ResponseType): boolean => {
  if (cached?.widget && !("extended_analytics" in cached)) {
    return false;
  }
  return true;
};

const getCachedData = (cacheKey: string): ResponseType | null => {
  const entry = cache.get(cacheKey);
  if (!entry) return null;

  const now = Date.now();
  const isExpired = now - entry.timestamp > CACHE_DURATION;

  if (isExpired) {
    // Clear expired entry
    clearTimeout(entry.timeoutId);
    cache.delete(cacheKey);
    return null;
  }

  return entry.data;
};

const setCacheData = (cacheKey: string, data: ResponseType): void => {
  // Clear existing cache entry if it exists
  const existingEntry = cache.get(cacheKey);
  if (existingEntry) {
    clearTimeout(existingEntry.timeoutId);
  }

  // Set up automatic purging after cache duration
  const timeoutId = setTimeout(() => {
    cache.delete(cacheKey);
  }, CACHE_DURATION);

  // Store new cache entry
  cache.set(cacheKey, {
    data,
    timestamp: Date.now(),
    timeoutId,
  });
};

const clearCache = (cacheKey: string): void => {
  const entry = cache.get(cacheKey);
  if (entry) {
    clearTimeout(entry.timeoutId);
    cache.delete(cacheKey);
  }
};

const useFetchAnalyticsWidgetData = ({
  dashboard,
  dashboardWidget,
  widgetUrl,
  filters,
  shouldFetch = true,
}: Params) => {
  const { company } = useSelector((state: RootState) => state);
  const timezoneOffset = company?.company_details
    ? (company.company_details as CompanyDetails)?.time_zone_offset
    : undefined;
  const [additionalFiltersState, setAdditionalFiltersState] = useState<
    AdditionalFilters[] | null
  >(null);
  const [data, setData] = useState<ResponseType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [lastFetchDuration, setLastFetchDuration] = useState<number | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track the last fetched URL to detect when we need to refetch after becoming visible
  const lastFetchedUrlRef = useRef<string | null>(null);

  // Ensure proper cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // Abort any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create the base URL for the API
  const getWidgetUrl = useCallback(
    (additionalFilters?: AdditionalFilters[]) => {
      if (dashboard && dashboardWidget && widgetUrl) {
        return configureUrlWithParams(
          `/api/v1/dar/${dashboard}/${dashboardWidget}/${widgetUrl}/widget`,
          {
            ...filters,
            ...(additionalFilters && { additionalFilters }),
            shouldUseEpocForTimeRange: company.shouldUseEpocForTimeRange,
            timezoneOffset,
          }
        );
      }
      return null;
    },
    [
      dashboard,
      dashboardWidget,
      widgetUrl,
      filters,
      company?.shouldUseEpocForTimeRange,
      timezoneOffset,
    ]
  );

  // Create the base URL for the API
  const apiUrl = useMemo(() => {
    return getWidgetUrl();
  }, [
    getWidgetUrl,
    dashboard,
    dashboardWidget,
    widgetUrl,
    JSON.stringify(filters),
  ]);

  // Fetch data function with caching
  const fetchDataWithCache = useCallback(
    async (url: string, skipCache: boolean = false): Promise<{ data: ResponseType; duration: number | null }> => {
      if (!url) {
        throw new Error("No URL provided");
      }

      const cacheKey = getCacheKey(url);

      // Check cache first unless skipCache is true
      if (!skipCache) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          if (isValidWidgetResponseCache(cachedData)) {
            return { data: cachedData, duration: null }; // null duration indicates cached response
          }
          clearCache(cacheKey);
        }
      }

      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      try {
        const startTime = Date.now();
        const response = await fetchData(url, {
          signal: abortControllerRef.current.signal,
        });
        const duration = Date.now() - startTime;

        const responseData = response as ResponseType;

        // Cache successful responses
        setCacheData(cacheKey, responseData);

        return { data: responseData, duration };
      } catch (error: any) {
        // Don't cache errors
        // Handle both native AbortError and Axios CanceledError (from AbortController)
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
          throw new Error("Request was aborted");
        }
        throw error;
      }
    },
    []
  );

  // Main data fetching effect
  // Only fetches when shouldFetch is true AND either:
  // 1. We haven't fetched this URL yet, OR
  // 2. The URL has changed since last fetch
  useEffect(() => {
    let isCancelled = false;

    const fetchMainData = async () => {
      if (!apiUrl) return;

      // Skip fetching if shouldFetch is false
      if (!shouldFetch) {
        return;
      }

      // Skip if we've already fetched this exact URL
      if (lastFetchedUrlRef.current === apiUrl) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { data: responseData, duration } = await fetchDataWithCache(apiUrl);

        if (!isCancelled && isMounted.current) {
          setData(responseData);
          setLastFetchDuration(duration);
          setError(null);
          lastFetchedUrlRef.current = apiUrl;
        }
      } catch (err: any) {
        if (!isCancelled && isMounted.current) {
          setError(err);
          console.error("Error fetching widget data:", err);
        }
      } finally {
        if (!isCancelled && isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    fetchMainData();

    return () => {
      isCancelled = true;
    };
  }, [apiUrl, fetchDataWithCache, shouldFetch]);

  // Handle additional filters whenever they change
  useEffect(() => {
    let isCancelled = false;

    if (additionalFiltersState) {
      const fetchFilteredData = async () => {
        setIsLoading(true);
        const url = getWidgetUrl(additionalFiltersState);

        if (url) {
          try {
            const { data: responseData, duration } = await fetchDataWithCache(url);

            if (!isCancelled && isMounted.current) {
              setData(responseData);
              setLastFetchDuration(duration);
              setAdditionalFiltersState(null);
              setError(null);
            }
          } catch (err: any) {
            if (!isCancelled && isMounted.current) {
              setError(err);
              setAdditionalFiltersState(null);
              console.error("Error fetching data with filters:", err);
            }
          } finally {
            if (!isCancelled && isMounted.current) {
              setIsLoading(false);
            }
          }
        } else {
          setAdditionalFiltersState(null);
          setIsLoading(false);
        }
      };

      fetchFilteredData();
    }

    return () => {
      isCancelled = true;
    };
  }, [additionalFiltersState, getWidgetUrl, fetchDataWithCache]);

  const isInputRequired = useMemo(() => {
    return data?.result_meta?.result_type === "missing_input";
  }, [data]);

  const overrideMessage = useMemo(() => {
    return data?.result_meta?.override_message || "";
  }, [data]);

  const filterUsedByWidget = useMemo(() => {
    return data?.result_meta?.filtered_by || null;
  }, [data]);

  const toolTipMessage = useMemo(() => {
    return data?.result_meta?.tooltip_message || "";
  }, [data]);

  const reloadData = useCallback(() => {
    if (!apiUrl) return;

    setIsLoading(true);
    setError(null);

    const cacheKey = getCacheKey(apiUrl);
    clearCache(cacheKey);

    fetchDataWithCache(apiUrl, true)
      .then(({ data: responseData, duration }) => {
        if (isMounted.current) {
          setData(responseData);
          setLastFetchDuration(duration);
          setError(null);
        }
      })
      .catch((err: any) => {
        if (isMounted.current) {
          setError(err);
          console.error("Error reloading data:", err);
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setIsLoading(false);
        }
      });
  }, [apiUrl, fetchDataWithCache]);

  const getWidgetDataWithFilters = useCallback(
    (additionalFilters: AdditionalFilters[]) => {
      setAdditionalFiltersState(additionalFilters);
    },
    []
  );

  return {
    data,
    isInputRequired,
    overrideMessage,
    toolTipMessage,
    filterUsedByWidget,
    isValidating: isLoading, // Keep the same property name for backward compatibility
    error,
    reloadData,
    getWidgetDataWithFilters,
    lastFetchDuration,
  };
};

export { useFetchAnalyticsWidgetData };
